/**
 * @file AsanaProfileRow.tsx
 * @brief Profile → Connected apps → Asana, for a personal account: its way in
 *        to /app/integrations/asana (DES 21-10-2026). Web and the Mac app.
 *
 * @details
 * Company admins set Asana up in Company → Settings → Asana; this row is for
 * accounts with no company, which had no link to the page before. It shows
 * whether Asana is connected and where to-dos go, from GET
 * /connectors/status. Hidden when the server has no Asana app configured.
 *
 * @author DES
 * @date 21-10-2026
 */

import { Link } from "react-router-dom";
import { ChevronRight, ListTodo } from "lucide-react";
import { GridBand } from "@/features/analytics/components/GridBlock";
import { useConnectorStatus } from "./api";

export function AsanaProfileRow() {
  const status = useConnectorStatus();
  if (status.data && !status.data.configured) return null;
  const asana = status.data?.asana;
  const line = status.isLoading
    ? "Checking…"
    : asana?.connected
      ? asana.needsReconnect
        ? "Needs reconnecting"
        : asana.projectName
          ? `Connected · to-dos go to ${asana.projectName}`
          : "Connected · choose a project"
      : "Send meeting to-dos to Asana, with the moment each was agreed.";

  return (
    <div className="border-t border-border/60">
      <GridBand className="text-sm font-medium">
        Tools
        <span className="ml-1 font-normal text-muted-foreground">· Where your meeting to-dos can go.</span>
      </GridBand>
      <Link
        to="/app/integrations/asana"
        className="flex items-center gap-3 px-4 py-3.5 transition-colors hover:bg-muted/30 md:px-5"
      >
        <ListTodo className="h-5 w-5 shrink-0 text-brand" strokeWidth={1.75} />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium">Asana</span>
          <span className="block truncate text-xs text-muted-foreground">{line}</span>
        </span>
        <span className="text-sm font-medium text-foreground">{asana?.connected ? "Manage" : "Set up"}</span>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </Link>
    </div>
  );
}
